import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  CheckCircle, 
  XCircle, 
  UserPlus, 
  UserCheck,
  Crown, 
  AlertTriangle, 
  RefreshCw,
  Users, 
  PlayCircle,
  MessageCircle, 
  Heart,
  Video, 
  PhoneMissed,
  Activity, 
  Sparkles,
  Info,
  Check,
  Trash2,
  X
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

// Icon mapping based on notification type
const iconMap = {
  'user-plus': UserPlus,
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'user-check': UserCheck,
  'crown': Crown,
  'alert-triangle': AlertTriangle,
  'refresh-cw': RefreshCw,
  'users': Users,
  'play-circle': PlayCircle,
  'message-circle': MessageCircle,
  'heart': Heart,
  'video': Video,
  'phone-missed': PhoneMissed,
  'activity': Activity,
  'sparkles': Sparkles,
  'info': Info,
  'bell': Bell
};

// Color mapping
const colorMap = {
  'cyan': '#06B6D4',
  'green': '#22C55E',
  'red': '#EF4444',
  'yellow': '#FFA500',
  'orange': '#FF8C00',
  'pink': '#EC4899',
  'blue': '#3B82F6',
  'gray': '#6B7280'
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const [notifResponse, countResponse] = await Promise.all([
        api.get('/notifications/in-app?limit=20&include_read=false'),
        api.get('/notifications/in-app/count')
      ]);
      setNotifications(notifResponse.data.notifications || []);
      setUnreadCount(countResponse.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and periodically
  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read (which also deletes it)
      await api.post(`/notifications/in-app/${notification.id}/read`);
      
      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Navigate if there's a destination
      if (notification.navigate_to) {
        setIsOpen(false);
        navigate(notification.navigate_to);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      toast.error('Failed to process notification');
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/in-app/read-all');
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to clear notifications');
    }
  };

  // Delete single notification
  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/in-app/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get icon component
  const getIcon = (iconName) => {
    return iconMap[iconName] || Bell;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full transition-colors hover:bg-white/10"
        data-testid="notification-bell-button"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-sunrise-black" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white rounded-full px-1"
            style={{ background: '#EF4444' }}
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-80 sm:w-96 rounded-xl shadow-2xl z-50 overflow-hidden backdrop-blur-xl"
          style={{ 
            background: 'var(--dropdown-bg, rgba(255, 249, 230, 0.95))',
            border: '1px solid var(--card-border)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
          }}
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: '1px solid var(--card-border)' }}
          >
            <h3 className="font-semibold text-sunrise-black">Notifications</h3>
            {notifications.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs px-2 py-1 rounded-lg transition-colors flex items-center gap-1"
                style={{ color: '#FF8C00' }}
                data-testid="mark-all-read-button"
              >
                <Check className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-8 text-center text-sunrise-medium">
                <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-sunrise-medium">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No new notifications</p>
                <p className="text-xs mt-1">You're all caught up!</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const IconComponent = getIcon(notification.icon);
                const iconColor = colorMap[notification.color] || colorMap.gray;
                
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-white/5 group"
                    style={{ borderBottom: '1px solid var(--card-border)' }}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    {/* Icon */}
                    <div 
                      className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ background: `${iconColor}20` }}
                    >
                      <IconComponent className="w-4 h-4" style={{ color: iconColor }} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-sunrise-black line-clamp-1">
                        {notification.title}
                      </p>
                      <p className="text-xs text-sunrise-medium line-clamp-2 mt-0.5">
                        {notification.message}
                      </p>
                      <p className="text-xs text-sunrise-light mt-1">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                    
                    {/* Delete Button */}
                    <button
                      onClick={(e) => handleDeleteNotification(e, notification.id)}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-1 rounded-full transition-opacity hover:bg-red-500/20"
                      data-testid={`delete-notification-${notification.id}`}
                    >
                      <X className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div 
              className="px-4 py-2 text-center"
              style={{ borderTop: '1px solid var(--card-border)' }}
            >
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/settings');
                }}
                className="text-xs text-sunrise-light hover:text-sunrise-medium transition-colors"
              >
                Manage notification settings
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
